import React, { useEffect, useCallback, useRef } from "react";
import { Icon, Modal } from "antd";
import _ from "i18n";
import { connect } from "react-redux";
import TypeFilter from "../../../Filters/EventStats/BudgetManagement/TypeFilter";
import AnnualTargetManagement from "./AnnualTargetManagement";
import YearFilter from "../../../Filters/EventStats/BudgetManagement/YearFilter";
import BudgetResult from "./BudgetResult";
import GenerateFollowUp from "./GenerateFollowUp";
import BudgetMonths from "./BudgetMonths";
import TargetBudget from "./TargetBudget";
import FollowUp from "./FollowUp";
import {
  setOriginalValues,
  updateSetUp,
} from "../../../../actions/actions/eventStats";
import {
  checkAppTypesConfiguration,
  fetchBudgetData,
  updateFollowUp,
  deleteBudgetConfiguration,
  updateConfiguration,
} from "../../../../actions/thunks/eventStats";
import { CommonLoader } from "../../../common/CommonLoader";
import { showErrorNotification } from "../../../../services/utils";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";


const BudgetManagementModal = ({
  budgetManagement,
  showTargetBudget,
  followUp,
  handleModalVisibility,
  setOriginalValues,
  fetchBudgetData,
  updateFollowUp,
  checkAppTypesConfiguration,
  deleteBudgetConfiguration,
  updateConfiguration,
  updateSetUp,
}) => {
  const { year, appType, annualTargetId } = budgetManagement;
  const currentYear = new Date().getFullYear();

  const prevYear = useRef(year);
  const prevAppType = useRef(appType);
  const prevFetchIsError = useRef(budgetManagement.fetchBudgetData.isError);
  const prevGenerateIsError = useRef(budgetManagement.generateFollowUp.isError);
  const prevUpdateIsError = useRef(budgetManagement.updateFollowUp.isError);
  const prevDeleteIsError = useRef(budgetManagement.deleteBudgetConfiguration.isError);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleFetchBudgetData = useCallback(() => {
    fetchBudgetData(year, appType);
  }, [fetchBudgetData, year, appType]);

  const handleUpdateFollowUp = useCallback(() => {
    updateFollowUp(annualTargetId, budgetManagement.followUp);
  }, [updateFollowUp, annualTargetId, budgetManagement.followUp]);

  const onCancelModal = useCallback(() => {
    handleModalVisibility(false);
  }, [handleModalVisibility]);

  const onCancelFollowUp = useCallback(() => {
    setOriginalValues();
  }, [setOriginalValues]);

  const handleDeleteBudgetConfiguration = useCallback(() => {
    const { confirm } = Modal;
    const onOk = () => {
      deleteBudgetConfiguration(annualTargetId, year, appType);
      Modal.destroyAll();
    };

    confirm({
      title: _('DELETE_CONFIGURATION_QUESTION'),
      icon: <ExclamationCircleOutlined />,
      okText: _("confirm"),
      cancelText: _("cancel"),
      className: 'relanceActivationModal',
      onOk,
    });
  }, [deleteBudgetConfiguration, annualTargetId, year, appType]);

  const editConfig = useCallback(() => {
    const { totalHTVA, registrantsCount, pricePerUnit } = budgetManagement.globalBudget;
    updateConfiguration(annualTargetId, budgetManagement.followUp, registrantsCount, pricePerUnit, totalHTVA, budgetManagement.budgetPerMonth);
  }, [updateConfiguration, annualTargetId, budgetManagement]);

  // ─── Lifecycle Effects ────────────────────────────────────────────────────────

  // componentDidMount
  useEffect(() => {
    handleFetchBudgetData();
    checkAppTypesConfiguration(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate equivalents
  useEffect(() => {
    const { isError: fetchIsError } = budgetManagement.fetchBudgetData;
    const { isError: generateIsError } = budgetManagement.generateFollowUp;
    const { isError: updateIsError } = budgetManagement.updateFollowUp;
    const { isError: deleteIsError } = budgetManagement.deleteBudgetConfiguration;

    if (prevYear.current !== year) {
      checkAppTypesConfiguration(year);
    }

    if (prevAppType.current !== appType || prevYear.current !== year) {
      handleFetchBudgetData();
    }

    if (
      (fetchIsError === true && prevFetchIsError.current !== fetchIsError) ||
      (generateIsError === true && prevGenerateIsError.current !== generateIsError) ||
      (updateIsError === true && prevUpdateIsError.current !== updateIsError) ||
      (deleteIsError === true && deleteIsError !== prevDeleteIsError.current)
    ) {
      showErrorNotification(_('error'), _('errorOccured'));
    }

    prevYear.current = year;
    prevAppType.current = appType;
    prevFetchIsError.current = fetchIsError;
    prevGenerateIsError.current = generateIsError;
    prevUpdateIsError.current = updateIsError;
    prevDeleteIsError.current = deleteIsError;
  }, [year, appType, budgetManagement.fetchBudgetData.isError, budgetManagement.generateFollowUp.isError, budgetManagement.updateFollowUp.isError, budgetManagement.deleteBudgetConfiguration.isError]);

  // ─── Derived values ───────────────────────────────────────────────────────────

  const registrantsCounts = followUp.map((item) => item.registrantsCount);
  const pricePerUnit = followUp.map((item) => item.pricePerUnit);

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={true}
      width={'90%'}
      bodyStyle={{ height: '620px', padding: '0' }}
      centered={true}
      wrapClassName={'create-BM-modal'}
      maskClosable={false}
      footer={(budgetManagement.isUpdatingFollowUp || budgetManagement.isUpdatingSetUp) && (
        <div className={'flex w-full'}>
          <button className={'cancel-button'} onClick={onCancelFollowUp}>{_('cancel')}</button>
          <button
            className={'save-button ml-auto'}
            onClick={budgetManagement.isUpdatingSetUp ? editConfig : handleUpdateFollowUp}
            disabled={budgetManagement.updateFollowUp.processing || registrantsCounts.includes('') || pricePerUnit.includes('') || budgetManagement.updateBudgetConfiguration.processing}
          >
            <span>{_('save')}</span>
            {(budgetManagement.updateFollowUp.processing === true || budgetManagement.updateBudgetConfiguration.processing === true) &&
              <Icon className="ml-2" type="sync" spin />}
          </button>
        </div>
      )}
      onCancel={onCancelModal}
    >
      <div className={'create-BM-header light-blue-font px-4 py-3'}>{_('MANAGE_MY_TURNOVER')}</div>
      <div className={'create-BM-filters px-4 py-3'}>
        <div className={'create-BM-nav'}>
          <YearFilter />
          <TypeFilter />
        </div>
      </div>
      {
        (budgetManagement.fetchBudgetData.fetching === true || budgetManagement.deleteBudgetConfiguration.processing === true) ?
          <CommonLoader containerHeight={"484px"} iconFontSize={"50px"} iconColor={"#29394D"} marginTop={"149"} />
          : (budgetManagement.fetchBudgetData.isError === true) ?
            <div className={'flex items-center flex-column retry-container'} style={{ minHeight: '484px' }}>
              <div className={'retry-label mb-3'} style={{ marginTop: "139px" }}>{_('ERROR_OCCURED')}</div>
              <button type={"button"} className={'retry-button'} onClick={handleFetchBudgetData}>
                {_('RETRY')}
              </button>
            </div>
            : (budgetManagement.followUp.length === 0 && (budgetManagement.appType === 'all' || budgetManagement.year < currentYear)) ?
              <div className={'flex items-center justify-center'} style={{ minHeight: '150px' }}>
                <span className={'no-data-found'}>{_('NO_CONFIG_DONE')}</span>
              </div>
              :
              <React.Fragment>
                <div className={'create-BM-main-content px-4'}>
                  <div className={'create-BM-main-content-aside mr-3'}>
                    <AnnualTargetManagement />
                    {
                      budgetManagement.year >= currentYear && budgetManagement.followUp.length !== 0 && budgetManagement.appType !== 'all' && budgetManagement.isUpdatingSetUp === false &&
                      <div className={'flex'} style={{ justifyContent: 'space-between', gap: '10px' }}>
                        <button key="submit" className={'notSave w-full'} style={{ backgroundColor: '#fe3745' }} onClick={handleDeleteBudgetConfiguration}>
                          <DeleteOutlined className={'mr-1'} />
                          {_('delete')}
                        </button>
                        <button className={'notSave w-full'} style={{ backgroundColor: '#ffb06a' }} onClick={updateSetUp}>
                          <EditOutlined className={'mr-1'} />
                          {_('EDIT')}
                        </button>
                      </div>
                    }
                  </div>

                  <BudgetMonths showTargetBudget={showTargetBudget} />
                  {budgetManagement.showTargetBudget &&
                    <div className={'target-budget-component'}>
                      <TargetBudget isRealized={true} />
                    </div>
                  }

                  <div className={budgetManagement.appType === 'monthly_premium' ? 'target-budget-component-monthly-premium' : 'target-budget-component'}>
                    <TargetBudget isRealized={false} />
                  </div>

                  <div className={'follow-up-component'}>
                    {budgetManagement.followUp.length > 0 ? (
                      <FollowUp />
                    ) : (
                      <GenerateFollowUp />
                    )}
                  </div>

                  {budgetManagement.followUp.length > 0 &&
                    <div className={'create-BM-main-content-budget-result ml-3'}>
                      <BudgetResult type={"budget"} />
                      <hr />
                      <BudgetResult type={"adjustment"} />
                    </div>
                  }
                </div>
              </React.Fragment>
      }
    </Modal>
  );
};

const mapStateToProps = state => ({
  budgetManagement: state.budgetManagement,
  showTargetBudget: state.budgetManagement.showTargetBudget,
  followUp: state.budgetManagement.followUp,
});

const mapDispatchToProps = {
  setOriginalValues,
  fetchBudgetData,
  updateFollowUp,
  checkAppTypesConfiguration,
  deleteBudgetConfiguration,
  updateConfiguration,
  updateSetUp,
};

export default connect(mapStateToProps, mapDispatchToProps)(BudgetManagementModal);



// WEBPACK FOOTER //
// ./app/components/Admin/EventStats/BudgetManagement/BudgetManagementModal.js